<template>
	<Skeleton :paragraph="{ rows: 10 }" :title="false" :loading="loading">
		<Form :label-col="{ span: 12 }" :wrapper-col="{ span: 12 }" :colon="false">
			<FormItem :label="$t('settings.vrsSettingsForm.containerIpAddr')">
				<TypographyText v-if="statusData.containerIpAddr">{{ statusData.containerIpAddr }}</TypographyText>
				<TypographyText v-else type="secondary">{{ $t('settings.vrsSettingsForm.containerIpAddrEmpty') }}</TypographyText>

				<template v-if="adminAccess" #extra>
					<StatusDisplay
						:source="statusData"
						field="containerIpAddrStatus"
						prefix="settings.vrsSettingsForm.containerIpAddrStatus"
						:badgeDict="STATUSES"
					/>
					<Button type="link" @click="containerIpAddrClick()">
						<template #icon>
							<EditOutlined />
						</template>
					</Button><br>
					<TypographyText v-if="showContainerIpAddrStatusHint" type="secondary">{{ $t('settings.vrsSettingsForm.containerIpAddrStatusHint.' + statusData.containerIpAddrStatus) }}</TypographyText>
				</template>
			</FormItem>

			<FormItem :label="$t('settings.vrsSettingsForm.casterPort')">
				{{ statusData.casterPort }}
			</FormItem>
			<FormItem v-if="casterSourcetableLink" :label="$t('settings.vrsSettingsForm.casterSourcetableLink')">
				<a :href="casterSourcetableLink" target="_blank">{{ casterSourcetableLink }}</a>
			</FormItem>

			<DividerAutoDirection>{{ $t('settings.vrsSettingsForm.ftpRinexDownloadHint') }}</DividerAutoDirection>
			<FormItem :label="$t('settings.vrsSettingsForm.ftpPort')">
				{{ statusData.ftpPort }}
			</FormItem>
			<FormItem v-if="adminAccess" :label="$t('settings.vrsSettingsForm.ftpoutPassword')">
				<Button size="small" @click="onFtpoutPasswordReset">
					<template #icon>
						<RedoOutlined />
					</template>
					{{ $t('settings.vrsSettingsForm.ftpoutPasswordResetLabel') }}
				</Button>
				<span class="pd">{{ statusData.ftpoutPassword }}</span>
			</FormItem>
		</Form>
	</Skeleton>
	<IpPortAccessModal v-model="isModalVisible" :item="containerIpAddrSettingsForModal" :onUpdate="fetchSettings" />
</template>

<script>
import { ref, reactive, watch, computed } from 'vue';
import { Button, Skeleton, Form, FormItem, TypographyText, message } from 'ant-design-vue';
import { EditOutlined, RedoOutlined } from '@ant-design/icons-vue';

import { useI18n } from 'vue-i18n'

import Api from '@/api/Api';
import Utils from '@/api/Utils';

import { showErrorMessage } from '@/components/common/errorMessage';

import IpPortAccessModal from '@/components/settings/IpPortAccessModal';
import DividerAutoDirection from '@/components/common/DividerAutoDirection.vue';
import StatusDisplay from '@/components/common/StatusDisplay.vue';

const STATUSES = {
	'loading': 'processing',
	'manual': 'success',
	'auto': 'warning',
	'empty': 'error',
}


export default {
	components: {
		Button, Skeleton, Form, FormItem, TypographyText,
		EditOutlined, RedoOutlined,
		IpPortAccessModal,
		DividerAutoDirection, StatusDisplay,
	},
	emits: ['ftpoutPasswordUpdated'],
	props: {
		adminAccess: {
			type: Boolean,
			required: true,
		},
		loading: {
			type: Boolean,
			required: true,
		},
		status: {
			type: Object,
			required: true,
		},
		secureData: {
			type: Object,
			required: false,
			default: () => ({}),
		},
		fetchSettings: {
			type: Function,
			required: false,
		},
	},
	setup(props, { emit }) {
		const { t } = useI18n();
		const statusData = reactive({
			casterPort: '',
			containerIpAddr: '',
			containerIpAddrStatus: 'loading',
			ftpPort: '',
			ftpoutPassword: '',
		});
		const isModalVisible = ref(false);
		const containerIpAddrSettingsForModal = ref(null);
		watch(
			[() => props.status, () => props.secureData],
			([status, secureData]) => {
				Utils.updateObject(statusData, status);
				Utils.updateObject(statusData, secureData);
			},
		);
		const showContainerIpAddrStatusHint = computed(() => {
			return ['auto', 'empty'].includes(statusData.containerIpAddrStatus);
		});
		const containerIpAddrClick = () => {
			isModalVisible.value = true;
			containerIpAddrSettingsForModal.value = {
				key: 'container_ip_addr',
				value: statusData.containerIpAddr,
			};
		}
		const onFtpoutPasswordReset = () => {
			Api.vrsSettingsFtpoutPasswordResetPost(response => {
				if (response.status === 'ok') {
					emit('ftpoutPasswordUpdated', response.ftpoutPassword);
					message.success(t('settings.vrsSettingsForm.ftpoutPasswordResetSuccess'))
				} else {
					showErrorMessage(response.errors);
				}
			});
		}
		const casterSourcetableLink = computed(() => {
			if (!statusData.containerIpAddr || !statusData.casterPort) {
				return null;
			}
			return `http://${statusData.containerIpAddr}:${statusData.casterPort}`;
		});
		return {
			STATUSES,
			statusData,
			showContainerIpAddrStatusHint,
			isModalVisible,
			containerIpAddrSettingsForModal,
			containerIpAddrClick,
			onFtpoutPasswordReset,
			casterSourcetableLink,
		};
	},
}
</script>
